import { useCallback, useEffect, useState } from "react";
import type { Manifest, ManifestAsset } from "./types.js";

/**
 * Load the manifest. Flow:
 *   1. Fetch ./latest.json to get the current runId
 *   2. Try same-origin ./manifest__{runId}.json FIRST (works in both dev and
 *      prod when the picker is served from the CDN alongside the manifest)
 *   3. Fall back to the absolute manifestUrl from latest.json (catches the
 *      case where the picker is hosted elsewhere)
 *   4. Also fetch the admin blocklist (`./cdn/blocklist.json` via the nginx
 *      proxy) and exclude listed asset IDs from manifest.assets.
 *
 * An explicit VITE_MANIFEST_URL env var overrides everything (power-user escape).
 */
export function useManifest(): {
  manifest: Manifest | null;
  loading: boolean;
  error: string | null;
  blockedIds: Set<string>;
  refetchBlocklist: () => Promise<void>;
  addBlocked: (ids: string[]) => void;
  /** Optimistically merge a just-added/edited asset (from the admin API). */
  addOverlayAsset: (asset: ManifestAsset) => void;
} {
  const [manifest, setManifest] = useState<Manifest | null>(null);
  const [blockedIds, setBlockedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetchBlocklist = useCallback(async () => {
    const blocked = await fetchBlocklist();
    // Merge instead of replace — preserves any IDs we optimistically added
    // before the SP CDN cache caught up.
    setBlockedIds((prev) => new Set([...prev, ...blocked]));
  }, []);

  // Optimistic local update for after a commit. The SP Public CDN caches
  // blocklist.json for ~15 min, so we can't rely on refetchBlocklist alone
  // to hide just-removed items.
  const addBlocked = useCallback((ids: string[]) => {
    setBlockedIds((prev) => {
      const next = new Set(prev);
      for (const id of ids) next.add(id);
      return next;
    });
  }, []);

  // Merge a just-added/edited asset into local state immediately. The durable
  // copy lives in overlay.json (written by the admin API) and converges on the
  // next load; this gives the curator instant feedback without a refetch.
  const addOverlayAsset = useCallback((asset: ManifestAsset) => {
    setManifest((prev) => (prev ? applyOverlay(prev, [asset]) : prev));
  }, []);

  useEffect(() => {
    void (async () => {
      try {
        const devUrl = (import.meta as unknown as { env?: { VITE_MANIFEST_URL?: string } }).env
          ?.VITE_MANIFEST_URL;

        // Fetch blocklist + admin overlay in parallel with the manifest. Both
        // overlays are non-fatal — failures just mean no hides / no additions.
        const [m, b, overlay] = await Promise.all([
          loadManifest(devUrl),
          fetchBlocklist(),
          fetchOverlay(),
        ]);
        setManifest(applyOverlay(m, overlay));
        setBlockedIds(b);
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return { manifest, loading, error, blockedIds, refetchBlocklist, addBlocked, addOverlayAsset };
}

/** Apply admin overlay assets onto a manifest: add new, override existing by id. */
function applyOverlay(m: Manifest, overlay: ManifestAsset[]): Manifest {
  if (overlay.length === 0) return m;
  const byId = new Map(m.assets.map((a) => [a.id, a]));
  for (const a of overlay) byId.set(a.id, a);
  const assets = [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
  return { ...m, assets };
}

/**
 * Admin additions/edits, written to overlay.json in the Assets root by the
 * /admin API. Fetched through the same /cdn proxy as the manifest, with a
 * cache-buster so we never get a stale copy (the proxy serves it no-store).
 */
async function fetchOverlay(): Promise<ManifestAsset[]> {
  try {
    const res = await fetch(`./cdn/overlay.json?t=${Date.now()}`, { cache: "no-store" });
    if (!res.ok) return [];
    const json = await res.json();
    return Array.isArray(json?.assets) ? (json.assets as ManifestAsset[]) : [];
  } catch {
    return [];
  }
}

async function loadManifest(devUrl: string | undefined): Promise<Manifest> {
  if (devUrl) {
    const r = await fetchJson(devUrl);
    return isPointer(r) ? await fetchJson(r.manifestUrl) : (r as Manifest);
  }
  const pointer = (await fetchJson("./latest.json")) as { runId: string; manifestUrl: string };
  const sameOrigin = `./manifest__${pointer.runId}.json`;
  try {
    return await fetchJson<Manifest>(sameOrigin);
  } catch {
    return await fetchJson<Manifest>(pointer.manifestUrl);
  }
}

/**
 * Pull blocklist.json from SharePoint via the Cloudways /cdn/ reverse proxy.
 * The file is uploaded to the public Assets library by the admin commit flow.
 * Returns empty set on 404 (no blocklist yet) or any error.
 */
async function fetchBlocklist(): Promise<Set<string>> {
  try {
    const res = await fetch("./cdn/blocklist.json", { cache: "no-store" });
    if (!res.ok) return new Set();
    const json = await res.json();
    const ids: string[] = Array.isArray(json) ? json : (json.blockedIds ?? []);
    return new Set(ids);
  } catch {
    return new Set();
  }
}

async function fetchJson<T = unknown>(url: string): Promise<T> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Fetch ${url} failed ${res.status}`);
  return (await res.json()) as T;
}

function isPointer(x: unknown): x is { manifestUrl: string } {
  return typeof x === "object" && x !== null && "manifestUrl" in x && typeof (x as { manifestUrl: unknown }).manifestUrl === "string";
}
